import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Calendar, Clock, MapPin, Wand2, Loader2 } from 'lucide-react';
import { hapticSuccess } from '../native';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDayHeading = (dayStr) => {
  const d = new Date(`${dayStr}T00:00:00`);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const formatTime = (iso) => {
  if (!iso) return 'TBD';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const gameLabel = (game) => {
  const team1 = game.team1_name || 'TBD';
  const team2 = game.team2_name || 'TBD';
  if (game.game_type === 'pool') {
    return `${team1} vs ${team2}`;
  }
  const round = game.bracket_type === 'finals'
    ? 'Finals'
    : `${game.bracket_type === 'losers' ? 'Losers ' : ''}Round ${game.round_number}`;
  return `${round}: ${team1} vs ${team2}`;
};

const GameRow = ({ game, editable, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [time, setTime] = useState(game.scheduled_time ? game.scheduled_time.slice(0, 16) : '');
  const [field, setField] = useState(game.field || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const endpoint = game.game_type === 'pool' ? 'pool-games' : 'bracket-games';
      await axios.put(`${API}/${endpoint}/${game.id}/schedule`, {
        scheduled_time: time ? `${time}:00` : null,
        field: field || null
      });
      hapticSuccess();
      toast.success('Game updated');
      setEditing(false);
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update game');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 rounded-lg border bg-muted/30" data-testid="schedule-game-row">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{gameLabel(game)}</p>
        <Badge variant="outline" className="mt-1 text-[10px]">
          {game.game_type === 'pool' ? 'Pool Play' : 'Bracket'}
        </Badge>
      </div>

      {editable && editing ? (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="datetime-local"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-auto h-8 text-xs"
            data-testid="schedule-time-input"
          />
          <Input
            placeholder="Field"
            value={field}
            onChange={(e) => setField(e.target.value)}
            className="w-24 h-8 text-xs"
            data-testid="schedule-field-input"
          />
          <Button size="sm" className="h-8" onClick={save} disabled={saving} data-testid="schedule-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {formatTime(game.scheduled_time)}
          </span>
          <span className="flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" />
            {game.field || 'TBD'}
          </span>
          {editable && (
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditing(true)} data-testid="schedule-edit-btn">
              Edit
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Day-by-day schedule for a tournament, built from pool + bracket games.
 * Read-only by default (TournamentDetails' Schedule tab); pass editable to
 * get per-game time/field editing and an Auto-Schedule button (Tournament
 * Director Panel).
 */
export const TournamentSchedule = ({ tournamentId, editable = false }) => {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoScheduling, setAutoScheduling] = useState(false);

  const fetchSchedule = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tournaments/${tournamentId}/schedule`);
      setSchedule(res.data);
    } catch (error) {
      console.error('Failed to load schedule:', error);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const handleAutoSchedule = async () => {
    setAutoScheduling(true);
    try {
      const alreadyHasTimes = schedule?.days?.length > 0;
      const res = await axios.post(`${API}/tournaments/${tournamentId}/schedule/auto-assign`, {
        force: alreadyHasTimes
      });
      hapticSuccess();
      toast.success(res.data.message);
      await fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to auto-schedule');
    } finally {
      setAutoScheduling(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">Loading schedule...</CardContent>
      </Card>
    );
  }

  const hasAnyGames = schedule && (schedule.days.length > 0 || schedule.unscheduled_count > 0);

  if (!hasAnyGames) {
    return (
      <Card>
        <CardContent className="p-4 text-center py-8">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-muted-foreground">No games generated yet</p>
          <p className="text-sm text-muted-foreground">
            Generate pool play or a bracket first, then schedule games across the tournament's dates.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="tournament-schedule">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">
          {schedule.start_date === schedule.end_date
            ? formatDayHeading(schedule.start_date)
            : `${formatDayHeading(schedule.start_date)} — ${formatDayHeading(schedule.end_date)}`}
          {schedule.unscheduled_count > 0 && ` · ${schedule.unscheduled_count} unscheduled`}
        </p>
        {editable && (
          <Button size="sm" variant="outline" className="gap-2" onClick={handleAutoSchedule} disabled={autoScheduling} data-testid="auto-schedule-btn">
            {autoScheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            Auto-Schedule
          </Button>
        )}
      </div>

      {schedule.days.map((day) => (
        <Card key={day.date}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              {formatDayHeading(day.date)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {day.games.map((game) => (
              <GameRow key={game.id} game={game} editable={editable} onSaved={fetchSchedule} />
            ))}
          </CardContent>
        </Card>
      ))}

      {schedule.unscheduled_count > 0 && (
        <Card className="border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4" />
              Not yet scheduled ({schedule.unscheduled_count})
            </CardTitle>
          </CardHeader>
          {editable && (
            <CardContent className="space-y-2">
              {schedule.unscheduled.map((game) => (
                <GameRow key={game.id} game={game} editable={editable} onSaved={fetchSchedule} />
              ))}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};
